import { EyeTrackingCanvasXCognitiveLoad as EyeTrackingCanvasXCognitiveLoadComponent } from "heat-next";
import { Vector3 } from "three";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

interface EyeTrackingCanvasXCognitiveLoadProps {
    isInsideModal?: boolean;
    subDimensionsData?: any;
    bareBones?: boolean;
}

const EyeTrackingCanvasXCognitiveLoad = ({
    isInsideModal,
    subDimensionsData,
    bareBones,
}: EyeTrackingCanvasXCognitiveLoadProps) => {
    const {
        gazeData,
        isFetchingGazeData,
        isErrorGazeData,
        customChartsData,
        isErrorCustomCharts,
        isFetchingCustomCharts,
        combinedCustomChartsData,
        isErrorCombinedCustomCharts,
        isFetchingCombinedCustomCharts,
    } = useSessionDetailsContext();

    //EyeTrackingCanvas
    //filter required object from subDimensionData objects
    const eyeTrackingCanvas = isInsideModal
        ? subDimensionsData?.flat()?.filter((item: any) => item?.chartType === "EyeTracking")
        : gazeData;

    //CognitiveLoad
    const customCharts = isInsideModal
        ? subDimensionsData?.flat().filter((item: any) => item?.chartType === "Gauge")
        : customChartsData;

    //CognitiveLoad
    const combinedCustomCharts = isInsideModal
        ? subDimensionsData?.flat().filter((item: any) => item?.chartType === "Timeline")
        : combinedCustomChartsData;

    //Conversion function to turn the values array from gazeData
    // into the accepted format for the Eye Tracking component
    function convertToLineSegments(data: any) {
        const lines = [];

        for (let i = 0; i < data?.length - 1; i++) {
            const curr = data[i];
            const next = data[i + 1];

            const from = new Vector3(curr.x, curr.y, curr.z);
            const to = new Vector3(next.x, next.y, next.z);

            lines.push({
                from: { x: from.x, y: from.y },
                to: { x: to.x, y: to.y },
                captureDate: new Date(curr.timestamp).toISOString(),
            });
        }

        return lines;
    }

    const getEyeTrackingData = (circuit: any) => {
        const eyeTrackingDataPerCircuit = eyeTrackingCanvas?.filter((entry: any) => {
            return entry?.title === circuit?.title;
        });

        const eyeTracking = {
            data: {
                canvas: {
                    width: 496,
                    height: 288,
                    canvasFillColor: "#383838",
                },
                images: [
                    {
                        imageSrc: "aviation/cockpit",
                        centerX: 70,
                        centerY: 16,
                        width: 355.1436,
                        height: 254.1132,
                        opacity: 1,
                    },
                ],
                linesLayer: {
                    lines: convertToLineSegments(eyeTrackingDataPerCircuit?.[0]?.values), //Transform gaze_data for EyeTracking Canvas
                    defaultColor: "#c56e38",
                    defaultThickness: 3,
                    hasGradient: true,
                },
                shapeProps: {
                    shapeName: "Circle",
                    threshold: 5,
                    baseRadius: 10,
                    maxRadius: 10,
                    growthFactor: 10,
                    color: "#c56e38",
                    hasGradient: true,
                },
            },
        };

        return eyeTracking;
    };

    //Create a combined cognitiveLoad dataset to animate the cognitive load gauge
    //Get the cognitiveLoad data from combinedCustomChartsData
    const cognitiveTimelineData = (combinedCustomCharts || []).reduce(
        (acc: any[], customData: any) => {
            const matches = (customData.datasets || []).filter(
                (item: any) => item.name === "CognitiveLoad",
            );

            return acc.concat(matches);
        },
        [],
    );

    //Build an array of timestamped score objects to insert into the combined cognitive load data
    const cognitiveTimeData = (cognitiveTimelineData?.[0]?.data || []).map((item: any) => ({
        Score: Number(item.Data),
        DateTime: item.DateTime,
    }));

    const combinedCognitiveLoadData = { ...customCharts[0], datasets: cognitiveTimeData };

    //Prepare data for eyeTrackingXCognitiveLoad component per circuit
    const eyeTrackingXCognitiveLoadData = eyeTrackingCanvas?.map((circuit: any) => {
        return {
            title: circuit.title || "Eye Tracking Cognitive Load",
            eyeTrackingCanvasData: getEyeTrackingData(circuit),
            cognitiveLoadData: combinedCognitiveLoadData, //TODO: add function to accept multiple circuits
        };
    });

    const isEyeTrackingCanvasEmptyState =
        !isErrorGazeData && (!eyeTrackingCanvas || eyeTrackingCanvas?.values?.length === 0);

    const isCustomChartsDataEmptyState =
        !isErrorCustomCharts && (!customCharts || customCharts?.length === 0);

    const isCombinedCustomChartsDataEmptyState =
        !isErrorCombinedCustomCharts &&
        (!combinedCustomCharts || combinedCustomCharts?.length === 0);

    const isEmptyState = () => {
        if (isInsideModal) {
            return subDimensionsData?.every((v: any) => v === undefined);
        } else if (
            isEyeTrackingCanvasEmptyState ||
            isCombinedCustomChartsDataEmptyState ||
            isCustomChartsDataEmptyState
        ) {
            return true;
        }
    };

    if (isFetchingCustomCharts || isFetchingCombinedCustomCharts || isFetchingGazeData) {
        return <ContentLoader height="21.1875rem" />;
    }

    if (
        (!isInsideModal && isErrorCustomCharts && !isFetchingCustomCharts) ||
        (!isInsideModal && isErrorCombinedCustomCharts && !isFetchingCombinedCustomCharts) ||
        (!isInsideModal && isErrorGazeData && !isFetchingGazeData)
    ) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="18.1875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isEmptyState()) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:eye-tracking:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="18.1875rem"
                />
            </ComponentWrapper>
        );
    }

    if (isFetchingCombinedCustomCharts || isFetchingCustomCharts || isFetchingGazeData) {
        return <ContentLoader height={"26.4375rem"} />;
    }

    return (
        <EyeTrackingCanvasXCognitiveLoadComponent
            dualPlaybackProps={{
                rowGap: "0.5rem",
                bareBones: bareBones,
            }}
            isInsideModal={isInsideModal}
            data={eyeTrackingXCognitiveLoadData}
        />
    );
};

export default EyeTrackingCanvasXCognitiveLoad;
